import Vue from 'vue'
import axios from 'axios'
import qs from 'qs'
import VueAxios from 'vue-axios'
import { API_URL } from '@/common/config'
import {AuthService} from '@/common/auth.service';
import {NotificationService as Notify} from '@/common/notification.service';
import router from "@/routes/router";
import { serialize } from 'object-to-formdata';
import store from '@/store/index.js';

const service = axios.create({
    baseURL: API_URL,
    timeout: 300000
})

service.interceptors.request.use(config => {
    if (AuthService.isAuthenticated()) {
      config.headers["Authorization"] = 'Bearer ' + AuthService.getToken();
      config.headers["X-Partner-Id"] = store.getters.partnerId;
    }
    return config
}, error => {
    Promise.reject(error)
})

const errorHanlders = {
    400: (response) => {
        let errorMessage = 'Unknown';
        let persist = false;
        if (response.data instanceof Blob) {
            // Specific handling for 400 status code with blob response
            const reader = new FileReader();
            reader.onload = () => {
                let errorMessage = reader.result;
                // Check if the errorMessage is JSON and get the error property if it exists
                try {
                    const parsedError = JSON.parse(errorMessage);
                    if (parsedError && parsedError.error) {
                    errorMessage = parsedError.error;
                    }
                } catch (e) {
                    // errorMessage is not JSON, use it as is
                }
                
                Notify.notifyError(errorMessage);
            };
            reader.readAsText(response.data);
            return;
        }
        else if(response.data && response.data.error)
        {
            errorMessage = response.data.error;
            persist = true;
        } 
        else if(response.data && response.data.title)
        {
            errorMessage = response.data.title;
            persist = true;
        } 
        else if (response.data){
            errorMessage =  response.data;
            persist = true;
        }
        if(persist) {
            Notify.notifyErrorManualClose(errorMessage);
        }else {
            Notify.notifyError(errorMessage);
        }
    },
    401: () => {
        router.push({
            name: 'Login'
        })
    },
    403: () => {
        router.push({
            name: 'Unauthorized'
        })
    },
    404: () => {
        //let errorMessage = "Item not found! Please reload the page and try again!";
        //Notify.notifyError(errorMessage);
        router.push({
            name: 'NotFound'
        })
    },
    500: (response) => {
        let errorMessage = 'Unknown';
        if(response.data.error)
        {
            errorMessage = response.data.error;
        } 
        else if (response.data){
            errorMessage =  response.data;
        }
        Notify.notifyError(errorMessage);
    },
    503: () => {
        let errorMessage = "The server is busy. Please try again!";
        Notify.notifyError(errorMessage);
    },
    'ECONNABORTED':() => {
        let errorMessage = "The request timeout. Please try again!";
        Notify.notifyError(errorMessage);
    },
    'DEFAULT': () => {
        let errorMessage = "Network error. Please try again!";
        Notify.notifyError(errorMessage);
    }
}

service.interceptors.response.use(
    response => {
        return response
    }, error => {
        
        let errCode = error.response?.status || error.code; 
        const handler = errorHanlders[errCode];
        if(handler) {
            handler(error.response);
        } else {
            errorHanlders['DEFAULT']();
        }
        return Promise.reject(error)
    })

const ApiService = {
    init () {
        Vue.use(VueAxios, axios)
        Vue.axios.defaults.baseURL = API_URL
    },
    
    setHeader () {
        Vue.axios.defaults.headers.common['Authorization'] = `Token ${JwtService.getToken()}`;
    },
    
    query (resource, params) {
        return service
        .get(resource, params)
    },
    
    get (resource, slug = '') {
        if(slug == undefined || slug == null || slug == '') {
            return service
            .get(`${resource}`)
        }
        return service
        .get(`${resource}/${slug}`)
    },

    getWithFilter (resource, slug = '', filter) {
        return service
        .get(`${resource}/${slug}`, {
            params: filter,
            paramsSerializer: params => {
                return qs.stringify(Object.fromEntries(Object.entries(params).filter(([k, v]) => v != null || v != undefined)))
            }
        })
    },

    post (resource, params) {
        return service.post(resource, params)
    },

    postWithDownload (resource, params) {
        return service.post(resource, params, 
            {
                responseType: 'blob'
            })
    },

    download (resource, slug = '') {
        return service.get(`${resource}/${slug}/content`, 
            {
                responseType: 'blob'
            })
    },

    postForm (resource, params) {
        let formData = new FormData();
        for ( var key in params ) {
            formData.append(key, params[key]);
        }
        return service.post(resource,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
    },

    postFormV2 (resource, params, responseType) {
        const formData = serialize(
            params,
            {
                indices: true,
            }
          );
        let config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
        };
        if(responseType) {
            config.responseType = responseType;
        }
        return service.post(resource,
            formData,
            config
            )
    },

    update (resource, slug, params) {
        return service.put(`${resource}/${slug}`, params)
    },
}

export default ApiService

export const FilterService = {
    slug: 'filters',
    get (id) {
        return ApiService.get(this.slug, id)
    },
    update (id, entity) {
        return ApiService.update(this.slug, id, entity)
    }
}

export const TLDService = {
    slug: 'tlds',
    get () {
        return ApiService.get(this.slug)
    },
}

export const TagService = {
    slug: 'tags',
    get (id) {
        return ApiService.get(this.slug, id)
    },
}

export const CategoryService = {
    slug: 'categories',
    get (id) {
        return ApiService.get(this.slug, id)
    },
    create (entity) {
        return ApiService.post(this.slug, entity)
    },
    update (id, entity) {
        return ApiService.update(this.slug, id, entity)
    },
    destroy (id) {
        return ApiService
          .delete(`${this.slug}/${id}`)
    }
}

export const UserService = {
    slug: 'users',
    get (id) {
        return ApiService.get(this.slug, id)
    }
}

export const WebsiteService = {
    slug: 'websites',
    getWithFilter (filter) {
        return ApiService.getWithFilter(this.slug, '', filter)
    },
}